/**
 * The affiliate programme, for the system administrator — every affiliate who
 * brought somebody, opened a Stripe account, was suspended or held, with what
 * each earned, waits for, can be paid and was paid, and the fraud signals it
 * shows (`affiliate-fraud.ts` on the Worker).
 *
 * Opening a row reads its detail: the signals in words, its customers (card
 * count per customer), its ledger and its payouts — and the levers:
 * Suspend / Reinstate (earns and is paid nothing), Hold / Release payouts
 * (keeps earning, nothing sent), Approve / Reject a payout waiting for review,
 * and a correction to the balance with its reason.
 *
 * A page of its own in the deployment's rail; the system overview says when
 * something here is waiting (`AffiliateAlert`).
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font } from '../theme/tokens';
import { GhostButton, Input, PrimaryButton, Row, RowAction, StatusCell, Table } from '../components/primitives';
import { notify } from '../components/Toast';
import { api, ApiError, apiPath } from '../lib/api';
import { isoDay, money } from '../lib/format';

interface AffiliateLine {
  userId: string;
  email: string;
  code: string;
  status: 'active' | 'suspended';
  stripeAccount: string | null;
  payoutsEnabled: boolean;
  payoutsHeld: boolean;
  inReview: number;
  signals: string[];
  referred: number;
  earnedCents: number;
  pendingCents: number;
  availableCents: number;
  paidCents: number;
}

interface Detail {
  signals: { key: string; text: string }[];
  customers: { kind: string; id: string; name: string; since: string | null; paidCents: number; invoices: number; cards: number }[];
  entries: { id: string; kind: string; invoiceId: string | null; baseCents: number; amountCents: number; note: string | null; createdAt: string }[];
  payouts: { id: string; amountCents: number; status: string; error: string | null; createdAt: string }[];
}

const COLUMNS = '1.6fr 70px 100px 100px 100px 100px 150px 150px';

async function act(body: Record<string, unknown>): Promise<boolean> {
  try {
    await api.post('/admin/actions', body);
    return true;
  } catch (error) {
    notify(error instanceof ApiError ? error.message : 'That did not work.');
    return false;
  }
}

/** What the administrator has to look at first: a payout waiting, then a signal, then a hold. */
function urgency(line: AffiliateLine): number {
  return line.inReview > 0 ? 0 : line.signals.length > 0 ? 1 : line.payoutsHeld ? 2 : 3;
}

/** On the system overview, only when a payout waits for a decision or an affiliate shows a signal. */
export function AffiliateAlert({ onOpen }: { onOpen: () => void }): React.JSX.Element | null {
  const [list, setList] = useState<AffiliateLine[]>([]);
  useEffect(() => {
    void api
      .get<{ affiliates: AffiliateLine[] }>('/admin/affiliates')
      .then((answer) => setList(answer.affiliates))
      .catch(() => setList([]));
  }, []);
  const waiting = list.reduce((sum, line) => sum + line.inReview, 0);
  const flagged = list.filter((line) => line.signals.length > 0).length;
  if (waiting === 0 && flagged === 0) return null;
  const words = [
    waiting > 0 ? `${waiting} payout${waiting === 1 ? ' waits' : 's wait'} for your review` : null,
    flagged > 0 ? `${flagged} affiliate${flagged === 1 ? ' shows' : 's show'} signs of abuse` : null
  ].filter(Boolean);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
      <StatusCell tone={flagged > 0 ? 'bad' : 'warn'} word="Affiliates" detail={words.join(' · ')} />
      <GhostButton size="sm" onClick={onOpen}>
        Open Affiliates
      </GhostButton>
    </div>
  );
}

export function Affiliates(): React.JSX.Element {
  const [list, setList] = useState<AffiliateLine[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = (): Promise<void> =>
    api
      .get<{ affiliates: AffiliateLine[] }>('/admin/affiliates')
      .then((answer) => setList([...answer.affiliates].sort((a, b) => urgency(a) - urgency(b))))
      .catch(() => setList([]));

  useEffect(() => void load(), []);

  if (!list) return <p style={{ fontSize: 13.5, color: color.inkFaint }}>Reading the affiliates…</p>;

  return (
    <>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 16px', lineHeight: 1.6, maxWidth: 720 }}>
        Accounts that brought a customer or set up payouts. Each earns half of what its customers pay excluding VAT, held
        120 days, the time a cardholder has to dispute a payment, paid by Stripe on the 1st from €50. A new affiliate's first
        payout, a payout of €1,000 or more, or one to an affiliate showing a signal, waits here for you to approve or reject. Open a row to hold its payouts, suspend it or correct its balance.
      </p>
      {list.length === 0 ? (
        <p style={{ fontSize: 13.5, color: color.inkFaint }}>Nobody has brought a customer or set up payouts yet, so nothing can be sent.</p>
      ) : (
        <Table columns={COLUMNS} header={['Affiliate', 'Brought', 'Earned', 'Waiting', 'Available', 'Paid', 'Payouts', 'Signals']}>
          {list.map((line) => (
            <div key={line.userId}>
              <Row columns={COLUMNS}>
                <div>
                  <div style={{ fontWeight: 500 }}>{line.email}</div>
                  <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>/r/{line.code}</div>
                </div>
                <span>{line.referred}</span>
                <span>{money(line.earnedCents, 'eur')}</span>
                <span>{money(line.pendingCents, 'eur')}</span>
                <span>{money(line.availableCents, 'eur')}</span>
                <span>{money(line.paidCents, 'eur')}</span>
                {line.status === 'suspended' ? (
                  <StatusCell tone="bad" word="Suspended" />
                ) : line.inReview > 0 ? (
                  <StatusCell tone="warn" word="To review" detail={`${line.inReview} payout(s)`} />
                ) : line.payoutsHeld ? (
                  <StatusCell tone="warn" word="Held" />
                ) : line.payoutsEnabled ? (
                  <StatusCell tone="ok" word="Ready" />
                ) : (
                  <StatusCell tone="off" ring word={line.stripeAccount ? 'Onboarding' : 'Not set up'} />
                )}
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {line.signals.length > 0 ? (
                    <StatusCell tone="bad" word={`${line.signals.length} signal${line.signals.length === 1 ? '' : 's'}`} detail={line.signals.join(', ')} />
                  ) : (
                    <span style={{ fontSize: 13, color: color.inkGhost }}>—</span>
                  )}
                  <RowAction onClick={() => setOpen(open === line.userId ? null : line.userId)}>{open === line.userId ? 'Close' : 'Open'}</RowAction>
                </span>
              </Row>
              {open === line.userId && <AffiliateDetail line={line} onChanged={load} />}
            </div>
          ))}
        </Table>
      )}
    </>
  );
}

function AffiliateDetail({ line, onChanged }: { line: AffiliateLine; onChanged: () => Promise<void> }): React.JSX.Element {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [cents, setCents] = useState('');
  const [reason, setReason] = useState('');
  const read = (): Promise<void> =>
    api
      .get<Detail>(apiPath`/admin/affiliates/${line.userId}`)
      .then(setDetail)
      .catch(() => notify('The affiliate could not be read.'));
  useEffect(() => void read(), [line.userId]);

  const after = async (ok: boolean): Promise<void> => {
    if (!ok) return;
    await Promise.all([read(), onChanged()]);
  };

  if (!detail) return <p style={{ padding: '12px 14px', fontSize: 13, color: color.inkFaint }}>Reading…</p>;
  const section = { fontSize: 12.5, fontWeight: 600, color: color.inkMuted, margin: '16px 0 6px' } as const;
  const small = { fontSize: 13, color: color.inkBody, lineHeight: 1.6 } as const;

  return (
    <div style={{ padding: '6px 14px 18px', background: color.surfaceAlt, borderRadius: 12, margin: '4px 0 10px' }}>
      <div style={section}>Signals</div>
      {detail.signals.length === 0 ? (
        <div style={small}>None.</div>
      ) : (
        detail.signals.map((signal) => (
          <div key={signal.key} style={{ ...small, color: color.badText }}>
            <span style={{ fontFamily: font.mono, fontSize: 12 }}>{signal.key}</span> — {signal.text}
          </div>
        ))
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
        <GhostButton
          size="sm"
          onClick={() => void act({ action: 'affiliate-hold', userId: line.userId, enabled: !line.payoutsHeld }).then(after)}
        >
          {line.payoutsHeld ? 'Release payouts' : 'Hold payouts'}
        </GhostButton>
        <GhostButton
          size="sm"
          onClick={() => void act({ action: 'affiliate-status', userId: line.userId, enabled: line.status === 'suspended' }).then(after)}
        >
          {line.status === 'active' ? 'Suspend' : 'Reinstate'}
        </GhostButton>
      </div>

      {detail.payouts.some((payout) => payout.status === 'review') && <div style={section}>Waiting for review</div>}
      {detail.payouts
        .filter((payout) => payout.status === 'review')
        .map((payout) => (
          <div key={payout.id} style={{ display: 'flex', alignItems: 'center', gap: 10, ...small }}>
            <span>
              {money(payout.amountCents, 'eur')} · {isoDay(payout.createdAt)}
            </span>
            <PrimaryButton size="sm" onClick={() => void act({ action: 'affiliate-review', payoutId: payout.id, enabled: true }).then(after)}>
              Approve and send
            </PrimaryButton>
            <GhostButton size="sm" onClick={() => void act({ action: 'affiliate-review', payoutId: payout.id, enabled: false }).then(after)}>
              Reject
            </GhostButton>
          </div>
        ))}

      <div style={section}>Customers</div>
      {detail.customers.length === 0 ? (
        <div style={small}>None yet.</div>
      ) : (
        detail.customers.map((customer) => (
          <div key={customer.id} style={small}>
            {customer.name} <span style={{ color: color.inkFaint }}>({customer.kind})</span> · since {isoDay(customer.since)} ·{' '}
            {customer.invoices} invoice(s), {money(customer.paidCents, 'eur')} excl. VAT · {customer.cards} card(s)
          </div>
        ))
      )}

      <div style={section}>Ledger</div>
      {detail.entries.slice(0, 30).map((entry) => (
        <div key={entry.id} style={small}>
          {isoDay(entry.createdAt)} · {entry.kind}
          {entry.note ? ` (${entry.note})` : ''} · {entry.amountCents < 0 ? '−' : ''}
          {money(Math.abs(entry.amountCents), 'eur')}
          {entry.invoiceId ? <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}> {entry.invoiceId}</span> : null}
        </div>
      ))}

      <div style={section}>Correct the balance</div>
      <form
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
        onSubmit={(event) => {
          event.preventDefault();
          const value = Math.round(Number(cents) * 100);
          if (!Number.isFinite(value) || value === 0) return notify('Give an amount in euros, negative to take back.');
          void act({ action: 'affiliate-adjust', userId: line.userId, cents: value, reason }).then(async (ok) => {
            if (ok) {
              setCents('');
              setReason('');
            }
            await after(ok);
          });
        }}
      >
        <Input aria-label="Amount in euros" placeholder="-25.00" value={cents} onChange={(event) => setCents(event.target.value)} style={{ width: 120 }} />
        <Input aria-label="Reason" placeholder="Reason" value={reason} onChange={(event) => setReason(event.target.value)} required style={{ width: 280 }} />
        <PrimaryButton size="sm" type="submit" tone="ink">
          Apply
        </PrimaryButton>
      </form>
    </div>
  );
}
